/**
 * Simple file download using a direct URL link.
 * Note: May open PDFs in browser instead of downloading them.
 * For guaranteed downloads of all file types, use `downloadFileFromUrl` instead.
 *
 * @param url - The URL of the file
 * @param fileName - The name to save the file as
 */
export const downloadFile = (url: string, fileName: string) => {
  const link = document.createElement("a")
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Downloads text content as a .txt file with auto-generated date-based filename.
 * Used for exporting chat conversations or other text content.
 *
 * @param content - The text content to download
 */
export const downloadBlob = (content: string) => {
  const blob = new Blob([content], { type: "text/plain" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  const date = new Date().toISOString().split("T")[0]
  link.download = `turnus-ai-chat-${date}.txt`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Downloads a file from a remote URL by fetching it as a blob first.
 * This ensures the file is downloaded rather than opened in the browser (especially for PDFs).
 *
 * Unlike `downloadFile`, this method fetches the file content first, which:
 * - Forces download behavior for all file types (including PDFs)
 * - Works correctly with CORS-enabled URLs
 * - Preserves original file content and mime type
 *
 * @param url - The remote URL of the file to download
 * @param fileName - The name to save the file as
 * @throws Error if the fetch fails or response is not ok
 */
export const downloadFileFromUrl = async (url: string, fileName: string) => {
  // Fetch the file as a blob to force download behavior
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.statusText}`)
  }

  const blob = await response.blob()

  const octetStreamBlob = new Blob([blob], {
    type: "application/octet-stream",
  })

  const blobUrl = URL.createObjectURL(octetStreamBlob)

  const link = document.createElement("a")
  link.href = blobUrl
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
}
